import { FormEvent, useEffect, useState } from "react";
import { Download, FileSpreadsheet, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import AdminLayout from "./AdminLayout";
import { AdminUser, FinanceReport, FinanceReportGroupBy, exportFinanceReportTrend, getCurrentAdmin, getFinanceReport, logout } from "./api";
import { orderStatusLabels } from "@/services/orders";

function formatCurrency(value: number): string {
  return `NT$ ${value.toLocaleString()}`;
}

function escapeCsvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function downloadTrendCsv(fileName: string, trend: FinanceReport["Trend"]) {
  const header = ["期間", "營收", "退款", "淨營收", "訂單數"];
  const rows = trend.map((point) =>
    [point.Period, String(point.GrossRevenue), String(point.RefundTotal), String(point.NetRevenue), String(point.OrderCount)]
      .map(escapeCsvField)
      .join(",")
  );
  const csvContent = "﻿" + [header.join(","), ...rows].join("\r\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function defaultStartDate(): string {
  const date = new Date();
  date.setDate(date.getDate() - 29);
  return date.toISOString().slice(0, 10);
}

function defaultEndDate(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function AdminFinanceReport() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [report, setReport] = useState<FinanceReport | null>(null);
  const [startDate, setStartDate] = useState(defaultStartDate());
  const [endDate, setEndDate] = useState(defaultEndDate());
  const [groupBy, setGroupBy] = useState<FinanceReportGroupBy>("day");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [exportingExcel, setExportingExcel] = useState(false);

  async function load(filters: { StartDate?: string; EndDate?: string; GroupBy?: FinanceReportGroupBy } = {}) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await getFinanceReport({
        StartDate: filters.StartDate ?? startDate,
        EndDate: filters.EndDate ?? endDate,
        GroupBy: filters.GroupBy ?? groupBy,
      });
      setReport(data);
      // 後端可能因為區間太長自動改成月彙總，跟前端選取的按鈕狀態同步，避免畫面顯示跟實際回傳資料不一致。
      setGroupBy(data.Range.GroupBy);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "財務報表載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ StartDate: startDate, EndDate: endDate, GroupBy: groupBy });
  }

  function handleExportCsv() {
    if (!report) return;
    downloadTrendCsv(`finance-report-${report.Range.StartDate}_${report.Range.EndDate}.csv`, report.Trend);
  }

  async function handleExportExcel() {
    if (!report) return;
    setExportingExcel(true);
    setPageMessage("");
    try {
      await exportFinanceReportTrend({ StartDate: report.Range.StartDate, EndDate: report.Range.EndDate, GroupBy: report.Range.GroupBy });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "匯出失敗，請稍後再試。");
    } finally {
      setExportingExcel(false);
    }
  }

  const summary = report?.Summary;

  return (
    <AdminLayout sectionLabel="帳務管理" title="財務報表" user={currentUser} onLogout={handleLogout}>
      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:flex md:flex-wrap md:items-end md:p-4" onSubmit={handleSearch}>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="finance-start">起始日期</label>
          <input id="finance-start" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        </div>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="finance-end">結束日期</label>
          <input id="finance-end" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
        </div>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="finance-groupby">彙總單位</label>
          <select id="finance-groupby" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={groupBy} onChange={(event) => setGroupBy(event.target.value as FinanceReportGroupBy)}>
            <option value="day">依日</option>
            <option value="month">依月</option>
          </select>
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          查詢
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60" disabled={!report} onClick={handleExportCsv} type="button">
          <Download size={18} />
          匯出趨勢 CSV
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60" disabled={!report || exportingExcel} onClick={handleExportExcel} type="button">
          <FileSpreadsheet size={18} />
          {exportingExcel ? "匯出中..." : "匯出趨勢 Excel"}
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      {loading ? (
        <div className="rounded-md border border-[#dfe6e1] bg-white p-8 text-center text-[#59665f]">載入中...</div>
      ) : !report || !summary ? (
        <div className="rounded-md border border-[#dfe6e1] bg-white p-8 text-center text-[#59665f]">查無資料。</div>
      ) : (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
              <p className="text-xs text-[#7b877f]">營收（毛額）</p>
              <p className="mt-1 text-2xl font-bold text-[#203d32]">{formatCurrency(summary.GrossRevenue)}</p>
              <p className="mt-1 text-xs text-[#7b877f]">{summary.OrderCount.toLocaleString()} 筆訂單</p>
            </div>
            <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
              <p className="text-xs text-[#7b877f]">退款總額</p>
              <p className="mt-1 text-2xl font-bold text-[#8a1f1f]">-{formatCurrency(summary.RefundTotal)}</p>
              <p className="mt-1 text-xs text-[#7b877f]">{summary.RefundCount.toLocaleString()} 筆退款</p>
            </div>
            <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
              <p className="text-xs text-[#7b877f]">淨營收</p>
              <p className="mt-1 text-2xl font-bold text-[#245438]">{formatCurrency(summary.NetRevenue)}</p>
            </div>
            <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
              <p className="text-xs text-[#7b877f]">運費／手續費／折扣</p>
              <p className="mt-1 text-sm text-[#59665f]">運費 {formatCurrency(summary.ShippingFeeTotal)}</p>
              <p className="text-sm text-[#59665f]">付款手續費 {formatCurrency(summary.PaymentExtraFeeTotal)}</p>
              <p className="text-sm text-[#59665f]">優惠折扣 -{formatCurrency(summary.DiscountAmount)}</p>
            </div>
          </div>

          <div className="mb-5 rounded-md border border-[#dfe6e1] bg-white p-4 md:p-5">
            <h2 className="mb-3 text-base font-bold text-[#315746]">營收趨勢</h2>
            <div className="h-[300px] w-full">
              <ResponsiveContainer height="100%" width="100%">
                <AreaChart data={report.Trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grossFill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="5%" stopColor="#315746" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#315746" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="refundFill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="5%" stopColor="#8a1f1f" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8a1f1f" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#edf0ee" vertical={false} />
                  <XAxis dataKey="Period" stroke="#7b877f" style={{ fontSize: 12 }} />
                  <YAxis stroke="#7b877f" style={{ fontSize: 12 }} tickFormatter={(value: number) => `${(value / 1000).toFixed(0)}k`} width={48} />
                  <Tooltip
                    formatter={(value: number, name: string) => [formatCurrency(value), name === "GrossRevenue" ? "營收" : name === "RefundTotal" ? "退款" : "淨營收"]}
                    labelFormatter={(label: string) => label}
                  />
                  <Legend formatter={(value: string) => (value === "GrossRevenue" ? "營收" : value === "RefundTotal" ? "退款" : "淨營收")} />
                  <Area dataKey="GrossRevenue" fill="url(#grossFill)" stroke="#315746" strokeWidth={2} type="monotone" />
                  <Area dataKey="RefundTotal" fill="url(#refundFill)" stroke="#8a1f1f" strokeWidth={2} type="monotone" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
              <h2 className="mb-3 text-base font-bold text-[#315746]">依付款方式</h2>
              <BreakdownTable rows={report.ByPaymentMethod.map((row) => ({ label: row.Label, count: row.OrderCount, amount: row.TotalAmount }))} />
            </div>
            <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
              <h2 className="mb-3 text-base font-bold text-[#315746]">依配送方式</h2>
              <BreakdownTable rows={report.ByShippingMethod.map((row) => ({ label: row.Label, count: row.OrderCount, amount: row.TotalAmount }))} />
            </div>
            <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
              <h2 className="mb-3 text-base font-bold text-[#315746]">依訂單狀態（不限付款狀態）</h2>
              <BreakdownTable rows={report.ByOrderStatus.map((row) => ({ label: orderStatusLabels[row.Status] || row.Status, count: row.OrderCount, amount: row.TotalAmount }))} />
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

function BreakdownTable({ rows }: { rows: { label: string; count: number; amount: number }[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-[#7b877f]">區間內沒有資料。</p>;
  }
  return (
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="border-b border-[#edf0ee] text-xs text-[#7b877f]">
          <th className="pb-2 font-normal">項目</th>
          <th className="pb-2 text-right font-normal">筆數</th>
          <th className="pb-2 text-right font-normal">金額</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#edf0ee]">
        {rows.map((row) => (
          <tr key={row.label}>
            <td className="py-2 text-[#59665f]">{row.label}</td>
            <td className="py-2 text-right text-[#59665f]">{row.count.toLocaleString()}</td>
            <td className="py-2 text-right font-bold text-[#203d32]">{formatCurrency(row.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
